import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

import { IconButton, Tooltip, TIconName, colors } from '@treezor/design-system';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';

import { openModal } from '@/store/actions/core.actions';
import { TFeatureSlugs } from '@/features/roles/store/models/features.model';
import { ISupportUser } from '@/features/dashboard-users/store/support-user.model';
import { IAdvancedRole, roleSlugValues } from '@/features/roles/store/models/advancedRole.model';

import { IDashboardUserActionModalProps } from '@/features/dashboard-users/components/DashboardUserModal.types';
import { IChangeDashboardUserPasswordModalProps } from '@/features/dashboard-users/components/changePassword/ChangeDashboardUserPasswordModal';
import { TAdvancedDropdownItem } from '@/ui-kit/dropdown/AdvancedDropdown';
import { AdvancedActionMenu } from '@/ui-kit/action-menu/AdvancedActionMenu';
import { IConfirmationReset2faModalProps } from '@/features/dashboard-users/components/manage/ConfirmationReset2faModal';
interface IActionItem {
  id: string;
  icon: TIconName;
  tooltip: string;
  onClick: () => void;
  isDisplayed?: boolean;
  isDisabled?: boolean;
  classname?: string;
}
interface IDashboardUserActionsProps {
  supportUser: ISupportUser;
  currentSupportUserRole: IAdvancedRole<TFeatureSlugs>;
  isOwnAccount: boolean;
  isActiveDashboardUserDisplayed?: boolean;
  onManageDashboardUser: () => void;
}

export function DashboardUserActions({
  supportUser,
  currentSupportUserRole,
  isOwnAccount,
  isActiveDashboardUserDisplayed = true,
  onManageDashboardUser,
}: IDashboardUserActionsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id: currentSupportUserId } = useCurrentSupportUser();

  const is2FAEnabled = useAppSelector((state) => !!state.dashboardConfig.config.settings['2fa']);
  const supportUserRole = supportUser?.metadata?.role?.slug;

  const toggleUnblockUserModal = () => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('dashboardUserUnblockModal', {
        supportUser,
      }),
    );
  };

  const toggleUpdatePasswordrModal = () => {
    dispatch(
      openModal<IChangeDashboardUserPasswordModalProps>('changeDashboardUserPasswordModal', {
        dashboardUser: supportUser,
      }),
    );
  };

  const toggleRemoveUserModal = () => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('dashboardUserRemoveModal', {
        supportUser,
      }),
    );
  };

  const toogleRestoreAccessModale = () => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('restoreDashboardUserAccessModal', {
        supportUser,
      }),
    );
  };

  const toggleSuspendUserModal = () => {
    dispatch(
      openModal<IDashboardUserActionModalProps>('suspendDashboardUserModal', {
        supportUser,
      }),
    );
  };

  const toogleReset2Fa = useCallback(() => {
    dispatch(
      openModal<IConfirmationReset2faModalProps>('confirmationReset2faModal', {
        dashboardUser: supportUser,
      }),
    );
  }, [dispatch, supportUser]);

  const actionList: IActionItem[] = [
    {
      id: 'support-user-actions-unblock-btn',
      icon: 'lock_open',
      tooltip: t('pages.administration.supportsUsers.tooltip.unblock'),
      onClick: toggleUnblockUserModal,
      isDisplayed: supportUser.wrongPwdAttempts >= 3,
    },
    {
      id: 'support-user-actions-suspend-btn',
      icon: 'block',
      tooltip: t('pages.administration.supportsUsers.tooltip.suspend'),
      onClick: toggleSuspendUserModal,
      isDisplayed: !isOwnAccount && supportUser.wrongPwdAttempts < 3,
      classname: 'w-[140px]',
    },
  ];

  const canUpdateAdmin =
    currentSupportUserRole?.slug === roleSlugValues.ADMINISTRATOR || supportUserRole !== roleSlugValues.ADMINISTRATOR;

  const canChangePassword = canUpdateAdmin && (is2FAEnabled ? !isOwnAccount : true);

  const canRemove = canUpdateAdmin && !isOwnAccount;

  const canReset2Fa =
    is2FAEnabled && !!supportUser.secureKey && (canUpdateAdmin || currentSupportUserId === supportUser.id);

  const menuButtons: TAdvancedDropdownItem[] = [
    ...(canChangePassword || canReset2Fa
      ? [
          {
            subItems: [
              ...(canChangePassword
                ? [
                    {
                      label: t('pages.administration.supportsUsers.changePassword.action'),
                      onClick: toggleUpdatePasswordrModal,
                    },
                  ]
                : []),
              ...(canReset2Fa
                ? [
                    {
                      label: t('pages.administration.supportsUsers.reset2Fa'),
                      onClick: toogleReset2Fa,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canRemove
      ? [
          {
            subItems: [
              {
                label: t('pages.administration.supportsUsers.details.moreActions.remove'),
                onClick: toggleRemoveUserModal,
                color: colors.red[500],
              },
            ],
          },
        ]
      : []),
  ];

  const MoreActionBtn =
    menuButtons.length > 0 ? (
      <AdvancedActionMenu actions={menuButtons} key={`action-menu-${currentSupportUserId}`} size="s" />
    ) : (
      <></>
    );

  return isActiveDashboardUserDisplayed ? (
    <div className="flex items-center gap-2 w-[86px]" key={`action-${currentSupportUserId}`}>
      <div className="flex-1">
        {actionList.map(({ id, tooltip, isDisplayed = true, isDisabled, classname = '', ...actionProps }) => {
          if (!isDisplayed) return <React.Fragment key={id} />;

          return (
            <Tooltip key={id} messageContent={tooltip} placement="top" contentClassName={clsx('z-[1000]', classname)}>
              <IconButton id={id} variant="secondary" size="s" {...actionProps} />
            </Tooltip>
          );
        })}
      </div>
      <div className="flex-1">{MoreActionBtn}</div>
      <div className="flex-1">
        <IconButton
          id="support-user-actions-manage-btn"
          variant="secondary"
          size="s"
          icon="chevron_right"
          onClick={() => onManageDashboardUser()}
          key={`support-user-actions-manage-btn-${currentSupportUserId}`}
        />
      </div>
    </div>
  ) : (
    <>
      <Tooltip
        key={`support-user-actions-restore-access-tooltip-${currentSupportUserId}`}
        messageContent={t('pages.administration.supportsUsers.tooltip.restoreAccess')}
        placement="top"
        contentClassName="z-[1000] min-w-[130px]"
      >
        <IconButton
          id="support-user-actions-restore-access-btn"
          variant="secondary"
          size="s"
          icon="lock_open"
          onClick={() => toogleRestoreAccessModale()}
          key={`support-user-actions-restore-access-btn-${currentSupportUserId}`}
        />
      </Tooltip>
      <Tooltip
        key={`support-user-actions-remove-tooltip-${currentSupportUserId}`}
        messageContent={t('pages.administration.supportsUsers.tooltip.remove')}
        placement="top"
        contentClassName="z-[1000] w-[140px]"
      >
        <IconButton
          id="support-user-actions-remove-btn"
          variant="secondary"
          size="s"
          icon="delete_forever"
          onClick={() => toggleRemoveUserModal()}
          key={`support-user-actions-remove-btn-${currentSupportUserId}`}
        />
      </Tooltip>
    </>
  );
}
